import {PersonListItem} from "@/services/models/Person";
import {PersonVisaInformation, PersonVisaFilterOptions, NewPersonVisaNote, PersonVisaAttachment, PersonVisaNote} from "@/services/models/PersonVisaInformation";
import {PagedData} from "@/services/models/shared/PagedData";
import {AxiosResponse} from "axios";
import BimsHttpService from "@/services/BimsHttpService";
import FileProvider from "@/utilities/providers/FileProvider";

class PersonVisaService {
    /**
     * @description Returns a paged list of people with some VISA information
     */
    getPeopleVisaList(filter: PersonVisaFilterOptions): Promise<PagedData<PersonListItem>> {
        return BimsHttpService.post(`/peoplevisas`, filter).then((response: AxiosResponse<PagedData<PersonListItem>>) => {
            return response.data as PagedData<PersonListItem>;
        });
    }

    /**
     * @description Returns the person with the specified id
     */
    getPersonVisaInformation(id: string): Promise<PersonVisaInformation> {
        return BimsHttpService.get(`/peoplevisas/${id}`).then((response: AxiosResponse<PersonVisaInformation>) => {
            return response.data as PersonVisaInformation;
        });
    }

    /**
     * @description Returns a list of person VISA notes.
     */
    getPersonVisaNoteList(personId: string): Promise<Array<PersonVisaNote>> {
        return BimsHttpService.get(`/peoplevisas/${personId}/Notes`).then((response: AxiosResponse<Array<PersonVisaNote>>) => {
            return response.data as Array<PersonVisaNote>;
        });
    }

    /**
     * @description Returns a list of person VISA attachments.
     */
    getPersonVisaAttachmentList(personId: string): Promise<Array<PersonVisaAttachment>> {
        return BimsHttpService.get(`/peoplevisas/${personId}/Attachments`).then((response: AxiosResponse<Array<PersonVisaAttachment>>) => {
            return response.data as Array<PersonVisaAttachment>;
        });
    }

    /**
     *  @description Adds new person visa note for the specified id
     */
    addPersonVisaNote(id: string, data: NewPersonVisaNote): Promise<string> {
        return BimsHttpService.post(`/peoplevisas/${id}/notes`,
            data
        ).then(response => {
            return response.data
        });
    } 

    /**
     *  @description Adds new person visa attachment for the specified id
     */
    addPersonVisaAttachment(id: string, data: any): Promise<string> {
        return BimsHttpService.put(`/peoplevisas/${id}/attachments`, data)
    } 

    /**
     *  @description Updates person visa information for the specified id
     */
    updatePersonVisaInformation(id: string, data: any): Promise<string> {
        return BimsHttpService.put(`/peoplevisas/${id}`, data)
    } 

    /**
     * @description Deletes the person visa note
     */
    deletePersonVisaNote(id: string): Promise<any> {
        return BimsHttpService.delete(`/peoplevisas/notes/${id}`)
    }

    /**
     * @description Marks the person visa attachment as deleted
     */
    deletePersonVisaAttachment(id: string): Promise<any> {
        return BimsHttpService.delete(`/peoplevisas/attachments/${id}`)
    }

    /**
     *  @description Downloads the person visa attachment with specified id
     */
    downloadPersonVisaAttachment (id: string): Promise<any> {
        return BimsHttpService.get(`/peoplevisas/Attachments/${id}`, { responseType: 'arraybuffer' })
            .then((response: AxiosResponse<any>) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;
        
                link.setAttribute("download", (response.headers['content-disposition'].split('filename=')[1].split(';')[0]).replace(/['"]+/g, ''));
                document.body.appendChild(link);
                link.click();
        
                // Remove the child after the doanload has completed
                document.body.removeChild(link);
            })
            .catch(error => {
              throw new TextDecoder('utf-8').decode(new Uint8Array(error.response.data)).replace(/['"]+/g, '')
            });
    }

    /**
     * @description Downloads the CSV export for visa people list
     */
    public getExcelExport(
        filter: PersonVisaFilterOptions
    ): Promise<any> {
        return BimsHttpService.post(
            `peoplevisas/export`, filter).then(response => {
            return FileProvider.ForceFileDownload(response)
        })
    }
}

export default new PersonVisaService()
